import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Phone,
  Globe,
  MapPin,
  Info,
  ShareNetwork,
  FileText,
  MapPinSimpleArea,
} from '@phosphor-icons/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import notify, { notifyIcons } from '../utils/notify';
import 'swiper/css';
import 'swiper/css/pagination';
import { getPlaceBySlug, trackPlaceEvent, getSimilarPlaces, getUiSettings } from '../services/api';
import telegramService from '../services/telegram';
import FavoriteButton from '../components/FavoriteButton';
import WorkingHoursModal from '../components/WorkingHoursModal';
import PlaceCard from '../components/PlaceCard';
import Loader from '../components/Loader';
import MarkdownLink from '../components/MarkdownLink';
import { getCurrentDayStatus } from '../utils/workingHours';
import { getPlaceDates } from '../utils/placeDates';
import { openInMaps } from '../utils/maps';
import { formatPhoneNumber } from '../utils/phone';

// Служебные действия ряда — круги 44px: минимум для касания
// (PROJECT_STRUCTURE, 44×44). При 52px ряд не помещался в строку
// и переносился. Иерархию с кнопкой сохранения держит не размер,
// а подпись и цвет.
//
// Глиф внутри — 24px, верхняя ступень ряда 16/20/24 (брендбук 9.3):
// при 20px иконки в круге 44px читались мелкими. Круг не растёт,
// растёт только глиф — так ряд остаётся в одну строку
const SECONDARY_ACTION_CLASS =
  'w-11 h-11 flex-shrink-0 bg-theme-bg-secondary rounded-full flex items-center ' +
  'justify-center hover:bg-theme-card-secondary transition-all active:scale-95';

// Обрезка описания по строкам, а не по символам: markdown рендерится
// целиком и не рвётся посреди **жирного**, край выходит ровный
const DESCRIPTION_CLAMP_LINES = 6;

// Кликабельная строка данных: адрес открывает карту, часы — модалку.
// Признак нажатия — только подсветка. Два варианта отклонены заказчиком:
// стрелка-шеврон справа (строка выглядела пунктом списка настроек)
// и иконка смысла слева (смотрится дёшево)
const FIELD_ROW_CLASS =
  'w-full flex items-center gap-2 -mx-2 px-2 py-2 rounded-md text-left ' +
  'transition-colors active:bg-theme-bg-secondary';

const PlacePage = () => {
  const { slug } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(false);
  const [showWorkingHoursModal, setShowWorkingHoursModal] = useState(false);
  const [similarPlaces, setSimilarPlaces] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showImageCaption, setShowImageCaption] = useState(null);
  const [uiSettings, setUiSettings] = useState(null);

  useEffect(() => {
    loadPlace();
    loadUiSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadUiSettings = async () => {
    try {
      const response = await getUiSettings();
      setUiSettings(response.data);
    } catch (err) {
      // Handle UI settings loading error silently
    }
  };

  const loadPlace = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPlaceBySlug(slug);

      if (response.data && response.data.length > 0) {
        const placeData = response.data[0];
        setPlace(placeData);

        // Трекаем просмотр места
        trackPlaceEvent(placeData.id, 'view');

        // Загружаем похожие места
        loadSimilarPlaces(placeData.id);
      } else {
        setError('Место не найдено');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadSimilarPlaces = async (placeId) => {
    try {
      setLoadingSimilar(true);
      const response = await getSimilarPlaces(placeId);
      setSimilarPlaces(response.data || []);
    } catch (err) {
      setSimilarPlaces([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleCall = () => {
    if (place.phone) {
      telegramService.hapticFeedback('medium');
      trackPlaceEvent(place.id, 'phone_click');

      // Copy phone to clipboard and show toast
      navigator.clipboard.writeText(place.phone).then(() => {
        // Копирование — обычное подтверждение, заливка не нужна (брендбук 8.2)
        notify('Номер скопирован', { icon: notifyIcons.copy });
      }).catch(() => {
        notify.error('Не удалось скопировать номер');
      });
    }
  };

  const handleWebsite = () => {
    if (place.website) {
      telegramService.hapticFeedback('light');
      trackPlaceEvent(place.id, 'website_click');
      telegramService.openLink(place.website);
    }
  };

  const handleMap = () => {
    telegramService.hapticFeedback('light');
    trackPlaceEvent(place.id, 'map_click');

    if (place.yandexMapsLink) {
      // Открываем ссылку на Яндекс.Карты из админки
      telegramService.openLink(place.yandexMapsLink);
    } else if (place.latitude && place.longitude) {
      // Фоллбэк на старый метод, если ссылка не указана
      openInMaps(place.latitude, place.longitude, place.title);
    }
  };

  const handleShare = () => {
    telegramService.hapticFeedback('light');
    trackPlaceEvent(place.id, 'share_click');
    const shareUrl = `https://t.me/spbneformal_app_bot/apps?startapp=place-${place.slug}`;
    const shareText = `Посмотри это место: ${place.title} 📍`;
    const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    telegramService.openTelegramLink(telegramShareUrl);
  };

  const handleBooking = () => {
    if (place.bookingUrl) {
      telegramService.hapticFeedback('medium');
      trackPlaceEvent(place.id, 'booking_click');
      telegramService.openLink(place.bookingUrl);
    }
  };

  const handleMenu = () => {
    if (place.menuUrl) {
      telegramService.hapticFeedback('light');
      telegramService.openLink(place.menuUrl);
    }
  };

  const getImageUrl = (imageData) => {
    if (!imageData) return 'https://via.placeholder.com/800x600?text=No+Image';

    // Handle both nested (imageData.data.url) and direct (imageData.url) structures
    const url = imageData.url || imageData.data?.url || imageData;

    if (!url) return 'https://via.placeholder.com/800x600?text=No+Image';

    return url.startsWith('http') ? url : url;
  };

  // Callback-ref: узел описания появляется только после загрузки места,
  // с обычным useRef на момент подписки он был бы ещё null. Считаем, вылезает
  // ли текст за обрезку — от этого зависит, нужна ли кнопка «Читать полностью».
  //
  // Замер только в свёрнутом виде: у развёрнутого scrollHeight равен
  // clientHeight, и кнопка «Свернуть» исчезла бы вместе с флагом.
  //
  // Пересчитываем на изменение размера узла: шрифт грузится с font-display:
  // swap, и первый замер может прийтись на метрики запасного шрифта —
  // после подмены высота строки меняется, а с ней и факт переполнения
  const measureDescription = useCallback((node) => {
    if (!node || isDescriptionExpanded) return undefined;

    const measure = () => {
      setIsDescriptionClamped(node.scrollHeight - node.clientHeight > 1);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isDescriptionExpanded]);

  const handlePromoCodeClick = (promoCode) => {
    if (!promoCode) return;

    navigator.clipboard.writeText(promoCode).then(() => {
      telegramService.hapticFeedback('medium');
      trackPlaceEvent(place.id, 'promo_copy');
      notify('Промокод скопирован', { icon: notifyIcons.copy });
    }).catch(() => {
      notify.error('Не удалось скопировать промокод');
    });
  };

  const renderSpecialOfferText = (text, promoCode) => {
    if (!text) return null;

    // Sanitize HTML to prevent XSS attacks
    const sanitizedText = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span'],
      ALLOWED_ATTR: []
    });

    // Если промокода нет, просто выводим текст
    if (!promoCode) {
      return <span dangerouslySetInnerHTML={{ __html: sanitizedText }} />;
    }

    // Ищем промокод в тексте. Промокод приходит из CMS, поэтому экранируем
    // спецсимволы: иначе «(» ломает рендер, а «(a+)+$» вешает вкладку.
    const escapedPromoCode = promoCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = sanitizedText.split(new RegExp(`(${escapedPromoCode})`, 'gi'));

    return (
      <>
        {parts.map((part, index) => {
          if (part.toLowerCase() === promoCode.toLowerCase()) {
            return (
              <span
                key={index}
                className="font-bold cursor-pointer hover:text-action transition-colors"
                onClick={() => handlePromoCodeClick(promoCode)}
                title="Нажмите чтобы скопировать"
              >
                {part}
              </span>
            );
          }
          return <span key={index} dangerouslySetInnerHTML={{ __html: part }} />;
        })}
      </>
    );
  };

  // Loading state
  if (loading) {
    return <Loader fullscreen size="large" />;
  }

  // Error state
  if (error || !place) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <div className="text-center px-4">
          <MapPinSimpleArea size={48} weight="light" className="mx-auto mb-4 text-theme-text-hint" />
          <p className="text-h3 text-theme-text-secondary mb-2">{error || 'Место не найдено'}</p>
          <p className="text-body-sm text-theme-text-hint mb-6">
            Проверьте правильность ссылки или попробуйте позже
          </p>
        </div>
      </div>
    );
  }

  // Handle images - support both Strapi v4 (data wrapper) and v5 (direct) formats
  const images = Array.isArray(place.images) ? place.images : (place.images?.data || []);
  const hasImages = images.length > 0;
  const workingHoursStatus = place.workingHours ? getCurrentDayStatus(place.workingHours) : null;
  const placeDates = getPlaceDates(place);

  // Get category name
  const categoryName = place.category?.name || place.category?.data?.name || '';

  // Get tags
  const tags = Array.isArray(place.tags) ? place.tags : (place.tags?.data || []);

  return (
    <div className="min-h-screen bg-theme-bg pb-8">
      {/* Image Gallery with Navigation */}
      {hasImages && (
        <section className="relative" style={{ height: '60vh', minHeight: '400px' }}>
          <Swiper
            modules={[Pagination]}
            pagination={images.length > 1 ? { clickable: true } : false}
            className="h-full image-swiper"
            onSlideChange={() => setShowImageCaption(null)}
            allowTouchMove={true}
          >
            {images.map((image, index) => {
              const caption = image.caption || image.data?.caption;
              return (
                <SwiperSlide key={index}>
                  <div className="relative w-full h-full">
                    <img
                      src={getImageUrl(image)}
                      alt={`${place.title} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {caption && (
                      <>
                        {/* Кнопка и подпись — одной строкой внизу слева,
                            на одной линии с точками пагинации. Подпись
                            по ширине текста, а не во всю ширину кадра:
                            широкая плашка перекрывала фото и выглядела
                            тяжело. Тёмная подложка одинакова в обеих темах —
                            под ней снимок, а не поверхность страницы */}
                        <div
                          className="swiper-no-swiping absolute left-3 flex items-center gap-2 max-w-[calc(100%-24px)]"
                          style={{ bottom: '24px', zIndex: 50 }}
                        >
                          <div
                            className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full cursor-pointer active:scale-95 transition-transform"
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              telegramService.hapticFeedback('light');
                              setShowImageCaption(showImageCaption === index ? null : index);
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              telegramService.hapticFeedback('light');
                              setShowImageCaption(showImageCaption === index ? null : index);
                            }}
                            style={{
                              // Без backdrop-filter — брендбук 5.4. Подложка
                              // плотнее: на светлом снимке белый глиф иначе
                              // не читается
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            }}
                          >
                            <Info size={16} className="text-white" style={{ pointerEvents: 'none' }} />
                          </div>
                          {showImageCaption === index && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="min-w-0 px-2.5 py-1.5 rounded-md"
                              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                            >
                              <p className="text-caption text-white truncate">
                                {caption}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>

        </section>
      )}

      {/* Main Content */}
      <div className="px-4">
        {/* Title & Category */}
        <section className="pt-5">
          <h1 className="text-h1 text-theme-text-primary mb-2">
            {place.title}
          </h1>
          <div className="text-caption text-theme-text-secondary">
            {categoryName}
            {tags && tags.length > 0 && (
              <>
                {' · '}
                {tags.map((tag) => tag.name).join(' · ')}
              </>
            )}
          </div>
        </section>

        {/* Special Offer */}
        {place.hasSpecialOffer && place.specialOffer && (
          <section className="mt-4">
            <div className="bg-theme-bg-secondary rounded-xl p-4 flex gap-3 items-center">
              {uiSettings?.specialOfferIcon && (
                <div className="relative flex-shrink-0">
                  <img
                    src={getImageUrl(uiSettings.specialOfferIcon)}
                    alt="Special offer"
                    className="w-5 h-5 object-contain"
                  />
                </div>
              )}
              <div className="text-sm leading-relaxed text-theme-text-primary flex-1">
                {renderSpecialOfferText(place.specialOffer, place.promoCode)}
              </div>
            </div>
          </section>
        )}

        {/* Action Buttons — «Сохранить» ключевое действие и идёт первым
            с подписью, остальные служебные кругами рядом. Одна строка
            без переноса; если контактов заполнено много и ряд шире
            экрана — горизонтальная прокрутка, а не вторая строка */}
        <section className="mt-5 flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-4 px-4">
          <FavoriteButton placeId={place.id} />
          {place.showPhone && place.phone && (
            <button
              onClick={handleCall}
              className={SECONDARY_ACTION_CLASS}
              title={formatPhoneNumber(place.phone)}
              aria-label={`Позвонить: ${formatPhoneNumber(place.phone)}`}
            >
              <Phone size={24} className="text-action" />
            </button>
          )}
          {place.showWebsite && place.website && (
            <button
              onClick={handleWebsite}
              className={SECONDARY_ACTION_CLASS}
              title="Открыть веб-сайт"
              aria-label="Открыть веб-сайт"
            >
              <Globe size={24} className="text-action" />
            </button>
          )}
          {place.showMap && (place.yandexMapsLink || (place.latitude && place.longitude)) && (
            <button
              onClick={handleMap}
              className={SECONDARY_ACTION_CLASS}
              title="Показать на карте"
              aria-label="Показать на карте"
            >
              <MapPin size={24} className="text-action" />
            </button>
          )}
          {place.showMenu && place.menuUrl && (
            <button
              onClick={handleMenu}
              className={SECONDARY_ACTION_CLASS}
              title="Открыть меню"
              aria-label="Открыть меню"
            >
              <FileText size={24} className="text-action" />
            </button>
          )}
          <button
            onClick={handleShare}
            className={SECONDARY_ACTION_CLASS}
            title="Поделиться"
            aria-label="Поделиться"
          >
            <ShareNetwork size={24} className="text-action" />
          </button>
        </section>

        {/* Description */}
        {place.description && (
          <section className="mt-6">
            {/* Отступ между абзацами 12px — как было изначально. Разметка
                одинакова в обоих состояниях, поэтому при раскрытии текст
                не перестраивается и не прыгает. Обрезка через line-clamp
                на -webkit-box: в WebKit/Blink (движок всех клиентов
                Telegram) он считает строки и по вложенным блокам,
                обрывая по целой строке */}
            <div
              ref={measureDescription}
              className="text-body text-theme-text-primary [&>*]:mb-3 [&>*:last-child]:mb-0"
              style={
                isDescriptionExpanded
                  ? undefined
                  : {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: DESCRIPTION_CLAMP_LINES,
                      overflow: 'hidden',
                    }
              }
            >
              {/* Ссылка на наш домен открывается внутри приложения, внешняя —
                  через Telegram. Своего обработчика здесь не было вовсе */}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>
                {place.description}
              </ReactMarkdown>
            </div>
            {isDescriptionClamped && (
              <button
                onClick={() => {
                  telegramService.hapticFeedback('light');
                  setIsDescriptionExpanded(!isDescriptionExpanded);
                }}
                className="text-action text-body-sm font-semibold mt-2 hover:underline"
              >
                {isDescriptionExpanded ? 'Свернуть' : 'Читать полностью'}
              </button>
            )}
          </section>
        )}

        {/* Сроки — тем же устройством, что адрес и часы: приглушённая
            подпись сверху, значение обычным весом (брендбук 3.4).
            Закончившееся место из лент убрано, но по ссылке открывается —
            тогда значение оранжевое: это предупреждение, а не ошибка */}
        {placeDates && (
          <section className="mt-5">
            <p className="text-caption text-theme-text-hint mb-1">Даты</p>
            <p
              className={`text-body tabular py-2 ${
                placeDates.status === 'ended'
                  ? 'text-warning'
                  : 'text-theme-text-primary'
              }`}
            >
              {placeDates.full}
            </p>
          </section>
        )}

        {/* Address — ярлык приглушён, значение основным цветом:
            читают адрес, а не слово «Адрес» (брендбук 3.4) */}
        {place.address && (
          <section className="mt-5">
            <p className="text-caption text-theme-text-hint mb-1">Адрес</p>
            <button
              onClick={handleMap}
              className={FIELD_ROW_CLASS}
              aria-label={`Открыть на карте: ${place.address}`}
            >
              <span className="text-body text-theme-text-primary text-left">
                {place.address}
              </span>
            </button>
            {place.metro && (
              <p className="text-caption text-theme-text-secondary mt-1 px-2">
                {place.metro}
              </p>
            )}
          </section>
        )}

        {/* Working Hours */}
        {place.workingHours && typeof place.workingHours === 'object' && Object.keys(place.workingHours).length > 0 && (
          <section className="mt-5">
            <p className="text-caption text-theme-text-hint mb-1">Часы работы</p>
            {workingHoursStatus && (
              <button
                onClick={() => {
                  telegramService.hapticFeedback('light');
                  setShowWorkingHoursModal(true);
                }}
                className={FIELD_ROW_CLASS}
                aria-label="Показать график работы"
              >
                <span className="text-body text-left" style={{ color: workingHoursStatus.color }}>
                  {workingHoursStatus.message}
                </span>
              </button>
            )}
          </section>
        )}

        {/* Booking Button */}
        {place.hasBooking && place.bookingUrl && (
          <section className="mt-6">
            <button
              onClick={handleBooking}
              className="w-full h-12 bg-action text-on-action text-label rounded-md hover:bg-action-pressed transition-all active:scale-98"
            >
              {place.bookingButtonLabel || 'Забронировать стол'}
            </button>
          </section>
        )}

        {/* Highlights */}
        {place.highlights && place.highlights.length > 0 && (
          <section className="mt-8">
            <h2 className="text-h2 text-theme-text-primary mb-4">
              Что интересного
            </h2>
            <div>
              <Swiper
                modules={[Pagination]}
                spaceBetween={12}
                slidesPerView="auto"
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                className="highlights-swiper"
              >
              {place.highlights.map((highlight, index) => (
                <SwiperSlide key={index} style={{ width: '180px' }}>
                  <div
                    className="bg-theme-card border border-theme-border rounded-lg overflow-hidden"
                    style={{
                      width: '180px',
                    }}
                  >
                    {/* Image Container - 180x200 with label */}
                    <div className="relative w-full" style={{ height: '200px' }}>
                      <img
                        src={getImageUrl(highlight.image)}
                        alt={highlight.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Метка на фото. Без backdrop-filter — брендбук 5.4:
                          размытие роняет плавность слайдера на Android.
                          Подложка вместо этого плотнее */}
                      <div
                        className="absolute bottom-2 left-2 flex items-center px-2 py-1 rounded-sm"
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }}
                      >
                        <span className="text-kicker text-white">
                          {highlight.label || 'Хит'}
                        </span>
                      </div>
                    </div>

                    {/* Text Content Below Image */}
                    <div className="bg-theme-card px-3 py-3">
                      <p className="text-label text-theme-text-primary leading-tight">
                        {highlight.title}
                      </p>
                      {highlight.description && (
                        <p className="text-caption text-theme-text-secondary mt-1">
                          {highlight.description}
                        </p>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
              </Swiper>
              <style>{`
                .highlights-swiper {
                  padding-bottom: 32px;
                }
                .highlights-swiper .swiper-pagination {
                  bottom: 0;
                }
                .highlights-swiper .swiper-pagination-bullet {
                  background: var(--color-action);
                  opacity: 0.3;
                }
                .highlights-swiper .swiper-pagination-bullet-active {
                  opacity: 1;
                }
                .image-swiper button {
                  pointer-events: auto !important;
                }
              `}</style>
            </div>
          </section>
        )}

        {/* Similar Places */}
        {!loadingSimilar && similarPlaces.length > 0 && (
          <section className="mt-8">
            <h2 className="text-h2 text-theme-text-primary mb-4">
              Вам может понравиться
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {similarPlaces.map((similarPlace) => (
                <PlaceCard key={similarPlace.id} place={similarPlace} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Working Hours Modal */}
      <WorkingHoursModal
        isOpen={showWorkingHoursModal}
        onClose={() => setShowWorkingHoursModal(false)}
        workingHours={place?.workingHours}
        placeName={place?.title}
      />
    </div>
  );
};

export default PlacePage;
